 
import { Payload } from ".";
import { AppDispatch } from "../../configureStore";
import { Prompt, PromptDetail, TestResult, TestPromptRequest } from "../../interface/prompts";
import execute from "../http/execute";
import failure from "../http/failure";
import { get, post, put, postReturnOpsMulti } from "../http/request";
import success from "../http/success";

 
interface PromptsState {
    prompts: Prompt[];
    selectedPrompt: PromptDetail | null;
    testResult: TestResult | null;
    loading: boolean;
    testLoading: boolean;
}

const initialState: PromptsState = {
    prompts: [],
    selectedPrompt: null,
    testResult: null,
    loading: false,
    testLoading: false,
};

// Action Types
export const GET_PROMPTS_REQUEST = 'prompts/GET_PROMPTS_REQUEST';
export const GET_PROMPTS_SUCCESS = 'prompts/GET_PROMPTS_SUCCESS';
export const GET_PROMPTS_FAILURE = 'prompts/GET_PROMPTS_FAILURE';

export const GET_PROMPT_CONTENT_REQUEST = 'prompts/GET_PROMPT_CONTENT_REQUEST';
export const GET_PROMPT_CONTENT_SUCCESS = 'prompts/GET_PROMPT_CONTENT_SUCCESS';
export const GET_PROMPT_CONTENT_FAILURE = 'prompts/GET_PROMPT_CONTENT_FAILURE';

export const UPDATE_PROMPT_REQUEST = 'prompts/UPDATE_PROMPT_REQUEST';
export const UPDATE_PROMPT_SUCCESS = 'prompts/UPDATE_PROMPT_SUCCESS';
export const UPDATE_PROMPT_FAILURE = 'prompts/UPDATE_PROMPT_FAILURE';

export const CREATE_PROMPT_REQUEST = 'prompts/CREATE_PROMPT_REQUEST';
export const CREATE_PROMPT_SUCCESS = 'prompts/CREATE_PROMPT_SUCCESS';
export const CREATE_PROMPT_FAILURE = 'prompts/CREATE_PROMPT_FAILURE';

export const TEST_PROMPT_REQUEST = 'prompts/TEST_PROMPT_REQUEST';
export const TEST_PROMPT_SUCCESS = 'prompts/TEST_PROMPT_SUCCESS';
export const TEST_PROMPT_FAILURE = 'prompts/TEST_PROMPT_FAILURE';

export const CLEAR_SELECTED_PROMPT = 'prompts/CLEAR_SELECTED_PROMPT';
export const CLEAR_TEST_RESULT = 'prompts/CLEAR_TEST_RESULT';

type ActionParams =
    | { type: typeof GET_PROMPTS_REQUEST }
    | { type: typeof GET_PROMPTS_SUCCESS; data: { prompts: Prompt[]; count: number } }
    | { type: typeof GET_PROMPTS_FAILURE }
    | { type: typeof GET_PROMPT_CONTENT_REQUEST }
    | { type: typeof GET_PROMPT_CONTENT_SUCCESS; data: PromptDetail }
    | { type: typeof GET_PROMPT_CONTENT_FAILURE }
    | { type: typeof UPDATE_PROMPT_REQUEST }
    | { type: typeof UPDATE_PROMPT_SUCCESS; data: { success: boolean; name: string; versionId: string }; content: string }
    | { type: typeof UPDATE_PROMPT_FAILURE }
    | { type: typeof CREATE_PROMPT_REQUEST }
    | { type: typeof CREATE_PROMPT_SUCCESS; data: { success: boolean; name: string; versionId: string } }
    | { type: typeof CREATE_PROMPT_FAILURE }
    | { type: typeof TEST_PROMPT_REQUEST }
    | { type: typeof TEST_PROMPT_SUCCESS; data: TestResult }
    | { type: typeof TEST_PROMPT_FAILURE; error?: string }
    | { type: typeof CLEAR_SELECTED_PROMPT }
    | { type: typeof CLEAR_TEST_RESULT };

function reducer(state = initialState, action: ActionParams): PromptsState {
    switch (action.type) {
        case GET_PROMPTS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case GET_PROMPTS_SUCCESS:
            return {
                ...state,
                prompts: action.data.prompts,
                loading: false,
            };
        case GET_PROMPTS_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case GET_PROMPT_CONTENT_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case GET_PROMPT_CONTENT_SUCCESS:
            return {
                ...state,
                selectedPrompt: action.data,
                loading: false,
            };
        case GET_PROMPT_CONTENT_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case UPDATE_PROMPT_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case UPDATE_PROMPT_SUCCESS:
            return {
                ...state,
                loading: false,
                selectedPrompt: state.selectedPrompt ? {
                    ...state.selectedPrompt,
                    versionId: action.data.versionId,
                    content: action.content,
                } : null,
            };
        case UPDATE_PROMPT_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case CREATE_PROMPT_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case CREATE_PROMPT_SUCCESS:
            return {
                ...state,
                loading: false,
            };
        case CREATE_PROMPT_FAILURE:
            return {
                ...state,
                loading: false,
            };
        case TEST_PROMPT_REQUEST:
            return {
                ...state,
                testLoading: true,
                testResult: null,
            };
        case TEST_PROMPT_SUCCESS:
            return {
                ...state,
                testLoading: false,
                testResult: action.data,
            };
        case TEST_PROMPT_FAILURE:
            return {
                ...state,
                testLoading: false,
                testResult: {
                    success: false,
                    error: action.error || 'Test failed',
                },
            };
        case CLEAR_SELECTED_PROMPT:
            return {
                ...state,
                selectedPrompt: null,
            };
        case CLEAR_TEST_RESULT:
            return {
                ...state,
                testResult: null,
            };
        default:
            return state;
    }
}

// Action Creators
export const getPrompts = (payload: Payload<any>) => (dispatch: AppDispatch) => {
    dispatch({ type: GET_PROMPTS_REQUEST });

    execute(get, '/prompts', payload.input, undefined, payload.overrideAuth)
        .then(success(dispatch, GET_PROMPTS_SUCCESS, payload))
        .catch(failure(dispatch, GET_PROMPTS_FAILURE, payload));
};

export const getPromptContent = (payload: Payload<{ promptName: string }>) => (dispatch: AppDispatch) => {
    dispatch({ type: GET_PROMPT_CONTENT_REQUEST });

    const promptName = encodeURIComponent(payload.input?.promptName || '');
    execute(get, `/prompts/${promptName}`, undefined, undefined, payload.overrideAuth)
        .then(success(dispatch, GET_PROMPT_CONTENT_SUCCESS, payload))
        .catch(failure(dispatch, GET_PROMPT_CONTENT_FAILURE, payload));
};

export const updatePrompt = (payload: Payload<{ promptName: string; content: string }>) => (dispatch: AppDispatch) => {
    dispatch({ type: UPDATE_PROMPT_REQUEST });

    const promptName = encodeURIComponent(payload.input?.promptName || '');
    const content = payload.input?.content || '';
    execute(put, `/prompts/${promptName}`, { content }, undefined, payload.overrideAuth)
        .then(success(dispatch, UPDATE_PROMPT_SUCCESS, { ...payload, content }))
        .catch(failure(dispatch, UPDATE_PROMPT_FAILURE, payload));
};

export const createPrompt = (payload: Payload<{ name: string; content: string }>) => (dispatch: AppDispatch) => {
    dispatch({ type: CREATE_PROMPT_REQUEST });

    execute(post, '/prompts', payload.input, undefined, payload.overrideAuth)
        .then(success(dispatch, CREATE_PROMPT_SUCCESS, payload))
        .catch(failure(dispatch, CREATE_PROMPT_FAILURE, payload));
};

export const testPrompt = (payload: Payload<TestPromptRequest>) => async (dispatch: AppDispatch) => {
    dispatch({ type: TEST_PROMPT_REQUEST });

    const { input } = payload;
    if (!input) {
        dispatch({ type: TEST_PROMPT_FAILURE, error: 'No input provided' });
        return;
    }

    // Build FormData for multipart upload
    const formData = new FormData();
    formData.append('type', input.type);
    formData.append('text', input.text);

    if (input.pdfFile) {
        formData.append('pdf', input.pdfFile);
    } else if (input.blobSource) {
        formData.append('blobSource', JSON.stringify(input.blobSource));
    }

    if (input.parserResults) {
        formData.append('parserResults', JSON.stringify(input.parserResults));
    }

    if (input.defaultFormInfo) {
        formData.append('defaultFormInfo', JSON.stringify(input.defaultFormInfo));
    }

    try {
        const response = await postReturnOpsMulti('/api/prompt-test', formData, undefined, undefined, payload.overrideAuth);
        const data = await response.json();

        if (response.ok) {
            dispatch({ type: TEST_PROMPT_SUCCESS, data });
            if (payload.onSuccess) payload.onSuccess(data);
        } else {
            dispatch({ type: TEST_PROMPT_FAILURE, error: data.error || data.message || 'Test failed' });
            if (payload.onError) payload.onError(data);
        }
    } catch (error: any) {
        dispatch({ type: TEST_PROMPT_FAILURE, error: error.message || 'Test failed' });
        if (payload.onError) payload.onError(error);
    }
};

export const clearSelectedPrompt = () => ({ type: CLEAR_SELECTED_PROMPT });
export const clearTestResult = () => ({ type: CLEAR_TEST_RESULT });

export default reducer;
